/**
 * batchTimeoutCohort.ts — WP-575 review round (P1, TEN-1126/TEN-2917).
 *
 * One self-contained responsibility: recognizing a batch entry whose `chain.createEntry`
 * gateway-timed-out, and keeping it in its own "outcome unknown" cohort everywhere a batch
 * result is reported — the split, the structured `unknownOutcomeEntries` mapping, the zod
 * shape, the "## Outcome Unknown — Not Failed" markdown section, and the summary note. It
 * must never be folded into `failed`/`failedEntries`, which every batch surface already
 * treats as "safe to retry individually" — that is the exact ghost-entry duplication
 * TEN-1126 exists to prevent.
 *
 * Shared by BOTH `tools/smart-capture.ts` (the real-create batch path) and
 * `lib/batchCaptureOutput.ts` (the preview batch path) — the preview path hits the SAME
 * unconditional try/catch a real batch does, so this cohort exists on both branches.
 * Extracted (STD-2 / DEC-1504 shrink-only) rather than duplicated in each: this
 * responsibility, written out twice, is what pushed both call sites over their
 * module-health budget — one module, one job, imported by both.
 */
import { z } from "zod/v3";
import { isGatewayTimeout, describeBatchEntryError } from "./captureTimeoutOutcome.js";

export type BatchTimeoutOutcome = {
  mayHaveLanded: boolean;
  budgetMs: number;
  elapsedMs: number;
};

/** The structured shape both batch surfaces emit under `unknownOutcomeEntries`. */
export type UnknownOutcomeEntry = {
  index: number;
  collection: string;
  name: string;
  error: string;
  mayHaveLanded: boolean;
  budgetMs: number;
  elapsedMs: number;
};

/**
 * Shared zod fragment — `batchCaptureRealOutputSchema` and `batchCapturePreviewOutputSchema`
 * both wrap this the same way (`z.array(unknownOutcomeEntrySchema).optional()`), so the
 * shape lives once instead of twice.
 */
export const unknownOutcomeEntrySchema = z.object({
  index: z.number(),
  collection: z.string(),
  name: z.string(),
  error: z.string(),
  mayHaveLanded: z.boolean(),
  budgetMs: z.number(),
  elapsedMs: z.number(),
});

/**
 * Did this catch observe a `chain.createEntry` gateway timeout? Carries the same
 * `mayHaveLanded`/`budgetMs`/`elapsedMs` signal the single-capture path already surfaces
 * (`captureTimeoutOutcome.ts`'s `buildCaptureOutcomeUnknownResult`) onto one batch entry.
 */
export function deriveTimeoutOutcome(error: unknown): BatchTimeoutOutcome | undefined {
  return isGatewayTimeout(error)
    ? { mayHaveLanded: error.mayHaveLanded, budgetMs: error.budgetMs, elapsedMs: error.elapsedMs }
    : undefined;
}

/** One call for a batch entry's catch: the error message plus its timeout signal, spread straight into `buildBatchEntryFailure`. */
export function describeBatchEntryOutcome(error: unknown, entryName: string): { error: string; timeoutOutcome?: BatchTimeoutOutcome } {
  return { error: describeBatchEntryError(error, entryName), timeoutOutcome: deriveTimeoutOutcome(error) };
}

/**
 * Splits a batch's not-ok cohort into genuinely-failed (untouched, caller's own shape) vs.
 * outcome-unknown (already mapped to the structured `unknownOutcomeEntries` shape) — one
 * call replaces a filter-by-`timeoutOutcome` plus a separate map at every call site.
 */
export function splitBatchNotOk<
  T extends { entryIdx: number; collection: string; name: string; error?: string; timeoutOutcome?: BatchTimeoutOutcome },
>(notOk: readonly T[]): { failed: T[]; unknownOutcome: UnknownOutcomeEntry[] } {
  return {
    failed: notOk.filter((r) => !r.timeoutOutcome),
    unknownOutcome: notOk.filter((r) => r.timeoutOutcome).map((r) => ({
      index: r.entryIdx,
      collection: r.collection,
      name: r.name,
      error: r.error ?? "unknown error",
      mayHaveLanded: r.timeoutOutcome!.mayHaveLanded,
      budgetMs: r.timeoutOutcome!.budgetMs,
      elapsedMs: r.timeoutOutcome!.elapsedMs,
    })),
  };
}

/** ", N outcome unknown" summary-line note — "" when the cohort is empty. Named distinctly from a "failed" note so a caller can never fold the two together. */
export function unknownOutcomeSummaryNote(entries: readonly unknown[]): string {
  return entries.length > 0 ? `, ${entries.length} outcome unknown` : "";
}

/**
 * The "## Outcome Unknown — Not Failed" markdown block both batch surfaces render — `[]`
 * when the cohort is empty, so a caller can always `lines.push(...renderOutcomeUnknownSection(...))`
 * unconditionally, no `if` needed. Distinct from "## Failed"/"## Would fail" on purpose:
 * these entries' writes may have landed, so they are never presented as safe to retry.
 */
export function renderOutcomeUnknownSection(entries: readonly UnknownOutcomeEntry[]): string[] {
  if (entries.length === 0) return [];
  // The footer follows the cohort's own `mayHaveLanded`, not the section's name (PR #533
  // review, Codex P2). On the PREVIEW batch path nothing can have landed, and this block is
  // rendered a few lines above that surface's own "_No DB writes_" note — so a fixed
  // duplicate-warning made one response state both. `some`, not `every`: a single entry that
  // may have landed is enough to owe the caller the search.
  const anyMayHaveLanded = entries.some((e) => e.mayHaveLanded);
  return [
    "",
    "## Outcome Unknown — Not Failed",
    ...entries.map((e) => `- ${e.name} [${e.collection}]: _${e.error}_`),
    "",
    anyMayHaveLanded
      ? "_Do NOT retry these individually — search for each name first (see `unknownOutcomeEntries` in the structured response); a retry may duplicate an entry that already landed._"
      : "_Nothing was written for these — the preview returns before any write, so re-running is safe. Only the validation verdict is missing._",
  ];
}
